import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { camelCaseEmptyStringRecursive } from '../../../services/utils';
import { fetchOvertimeCompensationsRequest } from '../../../requests/overtimeCompensations';

const useOvertimeCompensationData = currentEmployee => {
  const [overtimeData, setOvertimeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [totalRemainingBalance, setTotalRemainingBalance] = useState(0);
  const [overtimeEquivalence, setOvertimeEquivalence] = useState({ days: 0, hours: 0, totalOvertime: 0 });
  const dispatch = useDispatch();

  const parseMonthYear = useCallback(monthYearStr => {
    if (!monthYearStr) return null;
    if (monthYearStr.includes('/')) {
      const [month, year] = monthYearStr.split('/');
      return new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
    }
    if (monthYearStr.includes('-')) {
      const [year, month] = monthYearStr.split('-');
      return new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1);
    }
    return null;
  }, []);

  const fillMissingMonths = useCallback(
    originalData => {
      if (!originalData || !originalData.length) return [];
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const sixMonthsAgo = new Date(new Date(firstDayOfMonth).setMonth(firstDayOfMonth.getMonth() - 6));
      const dataMap = {};

      originalData.forEach(item => {
        const date = parseMonthYear(item.generatedRemunerationPeriod);
        if (!date) return;
        const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        dataMap[key] = item;
      });

      const requiredMonths = [];
      const currentDate = new Date(sixMonthsAgo);

      while (currentDate < firstDayOfMonth) {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth() + 1;
        const key = `${year}-${String(month).padStart(2, '0')}`;
        requiredMonths.push({ key, date: new Date(currentDate) });
        currentDate.setMonth(currentDate.getMonth() + 1);
      }

      const sampleItem = { ...originalData[0] };
      delete sampleItem.id;
      delete sampleItem.createdAt;
      delete sampleItem.updatedAt;

      return requiredMonths
        .map(month => {
          if (dataMap[month.key]) return dataMap[month.key];
          return {
            ...sampleItem,
            generatedRemunerationPeriod: `${String(month.date.getMonth() + 1).padStart(
              2,
              '0'
            )}/${month.date.getFullYear()}`,
            extraHoursGenerated: 0,
            expirationRemunerationPeriod: 0,
            extraHours: 0,
            remainingExtraHours: 0
          };
        })
        .sort((a, b) => parseMonthYear(a.generatedRemunerationPeriod) - parseMonthYear(b.generatedRemunerationPeriod));
    },
    [parseMonthYear]
  );

  const fetchDashboardCards = useCallback(
    (params = {}) => {
      setLoading(true);
      const today = new Date();
      const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const sixMonthsAgo = new Date(new Date(firstDayOfMonth).setMonth(firstDayOfMonth.getMonth() - 6));
      const formattedDateFrom = sixMonthsAgo.toISOString().split('T')[0];
      const sendParams = {
        ...params,
        filter_employee: currentEmployee,
        date_from: formattedDateFrom
      };

      fetchOvertimeCompensationsRequest({
        dispatch,
        params: sendParams,
        successCallback: response => {
          const formattedData = camelCaseEmptyStringRecursive(response.data);
          setOvertimeData({
            ...formattedData,
            data: fillMissingMonths(formattedData.data)
          });
          setLoading(false);
        },
        failureCallback: () => setLoading(false)
      });
    },
    [currentEmployee, dispatch, fillMissingMonths]
  );

  useEffect(() => {
    if (currentEmployee) {
      fetchDashboardCards();
    }
  }, [currentEmployee, fetchDashboardCards]);

  useEffect(() => {
    if (overtimeData?.data) {
      const dailyWorkingHours = overtimeData.data[0]?.dailyWorkingHours;
      const total = overtimeData.data.reduce((sum, row) => sum + (parseFloat(row.remainingExtraHours) || 0), 0);
      setTotalRemainingBalance(total);

      if (dailyWorkingHours && parseFloat(dailyWorkingHours) > 0) {
        const hoursDecimal = parseFloat(dailyWorkingHours);
        const days = Math.floor(total / hoursDecimal);
        const hours = total % hoursDecimal;
        setOvertimeEquivalence({ days, hours, totalOvertime: total, dailyWorkingHours });
      } else {
        setOvertimeEquivalence({ days: 0, hours: 0, totalOvertime: total });
      }
    }
  }, [overtimeData]);

  return {
    overtimeData,
    loading,
    totalRemainingBalance,
    overtimeEquivalence,
    fetchDashboardCards
  };
};

export default useOvertimeCompensationData;
